/**
 * A simulive replay as a signal flow: gstreamer reads the recorded game out of
 * S3 and pushes one SRT feed per stack; each TM stack writes HLS back to S3.
 *
 * The same primitives as the Test lab panel, arranged for a different pipeline —
 * no encoders and no gateway, because nothing dials in from outside. Lanes are
 * the stacks this launch actually started, which is the camera selection made in
 * the Start dialog.
 */

import { useState } from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';

import type {
  ReingestStack,
  ReingestStatus,
  ReingestTmStack,
} from '../hooks/useReingestStatusQuery';
import { isReplayActive } from '../hooks/useReingestStatusQuery';
import { resolveTmStack } from '../tmStacks';
import {
  FlowConnector,
  FlowModuleChips,
  FlowNodeCard,
  FlowStatusBadge,
  FlowTile,
  MONO_FONT,
  SignalFlowGrid,
  flowToken,
  type FlowColumn,
  type FlowLane,
  type FlowModule,
  type FlowTone,
} from './signal-flow';

const MODULES: FlowModule[] = [
  { key: 'source', label: 'S3 source' },
  { key: 'ingester', label: 'Ingester' },
  { key: 'stacks', label: 'TM stacks' },
  { key: 'output', label: 'HLS output' },
];

/**
 * What one stack is doing, as the replay's own vocabulary rather than TM's.
 *
 * `listening` and `ingesting` are the distinction the old panel could not draw:
 * it inferred liveness from whether an ingest URL had been written, which is
 * set once and never cleared, so a stack read as up long after it died.
 */
type LaneState =
  | 'idle'
  | 'launching'
  | 'listening'
  | 'ingesting'
  | 'finished'
  | 'error';

/**
 * Tone for the status text, tone for the connector, and whether the dashes
 * march.
 *
 * The two tones differ on `listening` on purpose: the stack is genuinely up
 * (green) while nothing is flowing into it yet (amber line). One tone would have
 * to lie about one of the two.
 */
const LANE_STYLE: Record<
  LaneState,
  { label: string; tone: FlowTone; lineTone: FlowTone; animated: boolean }
> = {
  idle: { label: 'idle', tone: 'idle', lineTone: 'idle', animated: false },
  launching: {
    label: 'launching…',
    tone: 'pending',
    lineTone: 'pending',
    animated: true,
  },
  listening: {
    label: 'listening',
    tone: 'active',
    lineTone: 'pending',
    animated: false,
  },
  ingesting: {
    label: 'ingesting',
    tone: 'active',
    lineTone: 'active',
    animated: true,
  },
  finished: { label: 'finished', tone: 'idle', lineTone: 'idle', animated: false },
  error: { label: 'error', tone: 'error', lineTone: 'error', animated: false },
};

/** The replay session's own states, for the ingester node. */
const INGESTER_LABELS: Record<string, { label: string; tone: FlowTone }> = {
  not_started: { label: 'not started', tone: 'idle' },
  stopped: { label: 'stopped', tone: 'idle' },
  queued: { label: 'queued', tone: 'pending' },
  tm_launching: { label: 'launching TM set…', tone: 'pending' },
  tm_ready: { label: 'stacks up — starting gstreamer…', tone: 'pending' },
  ingesting: { label: 'reading S3 — pushing SRT', tone: 'active' },
  stopping: { label: 'stopping…', tone: 'pending' },
  failed: { label: 'failed', tone: 'error' },
};

interface Lane extends FlowLane {
  /** `cam1`, `pgm`, … */
  feed: string;
  state: LaneState;
  /** TM's config name, when a launch recorded one. */
  name: string | null;
  /** TM's listener address, once the ingester resolved it. */
  ingestUrl: string | null;
  failure: string | null;
  egressBucket: string | null;
  egressPrefix: string | null;
}

/**
 * Reduce TM's state and the session's state to one lane state.
 *
 * Neither alone is enough: TM knows whether the stack is up, and only the
 * session knows whether gstreamer is pushing into it. A stack can be `running`
 * with nothing flowing — that is precisely `listening`.
 */
const laneStateFor = (
  tmState: string | null,
  sessionStatus: string,
): LaneState => {
  if (tmState === 'error') {
    return 'error';
  }

  if (tmState === 'running') {
    return sessionStatus === 'ingesting' ? 'ingesting' : 'listening';
  }

  if (sessionStatus === 'failed') {
    return 'error';
  }

  // TM is not running this feed. Whether that is "on its way up" or "not up" is
  // the session's answer, not TM's.
  if (isReplayActive(sessionStatus)) {
    return 'launching';
  }

  return tmState === 'finished' ? 'finished' : 'idle';
};

export default function SimuliveSignalFlow({ status }: { status: ReingestStatus }) {
  const theme = useTheme();
  const [enabled, setEnabled] = useState<string[]>(
    MODULES.map((module) => module.key),
  );

  const toggle = (key: string) =>
    setEnabled((current) =>
      current.includes(key)
        ? current.filter((entry) => entry !== key)
        : [...current, key],
    );

  const launched: ReingestStack[] = status.stacks ?? [];
  const observed: ReingestTmStack[] = status.tmStacks ?? [];

  /**
   * Every feed either source knows about, in launch order.
   *
   * The union rather than one or the other: `stacks` records what the launch
   * started, so a config TM created but never managed to start is missing from
   * it — and that is the lane most worth looking at. Feeds only TM knows about
   * are appended after the launched ones.
   */
  const feeds = [
    ...launched.map((stack) => stack.stackId),
    ...observed
      .map((stack) => stack.feed)
      .filter((feed) => !launched.some((stack) => stack.stackId === feed)),
  ].filter((feed, index, all) => all.indexOf(feed) === index);

  const lanes: Lane[] = feeds.map((feed) => {
    const record = launched.find((stack) => stack.stackId === feed);
    const { entry, state: tmState, failure } = resolveTmStack(
      observed.filter((stack) => stack.feed === feed),
    );

    return {
      key: feed,
      feed,
      state: laneStateFor(tmState, status.status),
      name: record?.name ?? null,
      ingestUrl: record?.ingestUrl ?? null,
      failure,
      egressBucket: entry?.egressBucket ?? null,
      egressPrefix: entry?.egressPrefix ?? null,
    };
  });

  const session = INGESTER_LABELS[status.status] ?? {
    label: status.status.replace(/_/g, ' '),
    tone: 'idle' as FlowTone,
  };

  const columns: Array<FlowColumn<Lane>> = [];

  if (enabled.includes('source')) {
    columns.push({
      key: 'source',
      title: 'Source',
      width: 'minmax(190px, auto)',
      cell: {
        span: 'lanes',
        render: () => (
          <FlowNodeCard
            title="S3 bucket"
            // The recording is a finished upload; it is either there or the
            // ingester could not read it, and the ingester's own state says so.
            // Nothing here is worth colouring green.
            tone="idle"
            lines={[
              status.sourceUri ?? 'resolved when the ingester starts',
            ]}
            note="Recorded game, one object per feed."
          />
        ),
      },
    });
    columns.push({
      key: 'source-link',
      width: 'minmax(88px, 1fr)',
      cell: {
        span: 'lanes',
        // One line, not one per lane: gstreamer opens the folder once and demuxes
        // from it. Drawing it per feed would claim a fan-out that happens on the
        // far side of this box.
        render: () => (
          <FlowConnector
            label="reads"
            tone={session.tone}
            animated={status.status === 'ingesting'}
          />
        ),
      },
    });
  }

  if (enabled.includes('ingester')) {
    columns.push({
      key: 'ingester',
      title: 'Ingester · EC2',
      width: 'minmax(210px, auto)',
      cell: {
        span: 'lanes',
        render: () => (
          <FlowNodeCard
            title="gstreamer"
            tone={session.tone}
            pulse={session.tone === 'pending'}
            statusLabel={session.label}
            lines={[status.instanceId, status.instanceIp].filter(Boolean)}
            note="Demuxes the recording and pushes one SRT feed per stack."
          />
        ),
      },
    });
    columns.push({
      key: 'ingester-link',
      width: 'minmax(96px, 1fr)',
      cell: {
        span: 'lane',
        render: (lane) => {
          const style = LANE_STYLE[lane.state];

          return (
            <FlowConnector
              label="SRT push"
              tone={style.lineTone}
              animated={style.animated}
            />
          );
        },
      },
    });
  }

  if (enabled.includes('stacks')) {
    columns.push({
      key: 'stacks',
      title: 'TiledMedia stacks',
      width: 'minmax(240px, 1.3fr)',
      cell: {
        span: 'lane',
        render: (lane) => (
          <FlowTile>
            <Typography
              variant="caption"
              sx={{ fontFamily: MONO_FONT, display: 'block', fontWeight: 500 }}
            >
              TM · {lane.name ?? lane.feed}
            </Typography>

            {/* The listener address gstreamer pushes into. Worth showing
                verbatim: pasting it into ffmpeg is how you test a stack without
                the ingester. */}
            {lane.ingestUrl && (
              <Stack
                direction="row"
                sx={{ alignItems: 'center', gap: 0.5, mt: 0.375 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: MONO_FONT,
                    color: flowToken(theme, 'wash200'),
                    wordBreak: 'break-all',
                  }}
                >
                  {lane.ingestUrl}
                </Typography>
                {/* flexShrink so the break-all URL cannot squeeze the label. */}
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ flexShrink: 0, minWidth: 0, px: 1, fontSize: 10 }}
                  onClick={() =>
                    navigator.clipboard?.writeText(lane.ingestUrl as string)
                  }
                >
                  Copy
                </Button>
              </Stack>
            )}

            <FlowStatusBadge
              label={LANE_STYLE[lane.state].label}
              tone={LANE_STYLE[lane.state].tone}
              // Not `animated`: that marches the connector because data is
              // moving, which `ingesting` does while already settled. Only
              // `pending` is a transition in flight.
              pulse={LANE_STYLE[lane.state].tone === 'pending'}
              sx={{ mt: 0.5 }}
            />

            {/* Inline rather than a tooltip, and the grid sets no row height, so
                a failing lane simply grows and stays aligned with its
                connectors. */}
            {lane.failure && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: flowToken(theme, 'red500'),
                    fontWeight: 600,
                  }}
                >
                  Last deployment failure
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: flowToken(theme, 'red500'),
                    fontFamily: MONO_FONT,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {lane.failure}
                </Typography>
              </Box>
            )}
          </FlowTile>
        ),
      },
    });
  }

  if (enabled.includes('output')) {
    if (enabled.includes('stacks')) {
      columns.push({
        key: 'output-link',
        width: 'minmax(88px, 1fr)',
        cell: {
          span: 'lane',
          render: (lane) => (
            <FlowConnector
              label="writes HLS"
              tone={LANE_STYLE[lane.state].lineTone}
              animated={lane.state === 'ingesting'}
            />
          ),
        },
      });
    }

    columns.push({
      key: 'output',
      title: 'HLS output',
      width: 'minmax(210px, 1.2fr)',
      cell: {
        span: 'lane',
        render: (lane) => (
          <FlowTile>
            <Typography
              variant="caption"
              sx={{
                fontFamily: MONO_FONT,
                display: 'block',
                color: flowToken(theme, 'wash200'),
                overflowWrap: 'break-word',
              }}
            >
              {lane.egressPrefix
                ? `s3://${lane.egressBucket}/${lane.egressPrefix}/`
                : 'no egress recorded'}
            </Typography>
            {lane.egressPrefix && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: MONO_FONT,
                  display: 'block',
                  mt: 0.375,
                  color: flowToken(theme, 'wash500'),
                }}
              >
                feed-0/master.m3u8
              </Typography>
            )}
          </FlowTile>
        ),
      },
    });
  }

  return (
    <Stack spacing={2}>
      <FlowModuleChips modules={MODULES} enabled={enabled} onToggle={toggle} />

      {lanes.length === 0 ? (
        // No stacks means no lanes to draw, and the stage cards alone would read
        // as a diagram that failed to load. Say what is actually true instead.
        <Typography variant="body2" sx={{ color: flowToken(theme, 'wash500') }}>
          {isReplayActive(status.status)
            ? 'Waiting for the first TiledMedia stack to be recorded…'
            : 'No stacks for this game — Start a replay to pick cameras.'}
        </Typography>
      ) : (
        <SignalFlowGrid columns={columns} lanes={lanes} />
      )}
    </Stack>
  );
}
